// Vendor Program v2 PR 1 — the loan Goals tab. Goals are LOAN-level; vendor
// assignment is a judgment call made here: a grouped-by-suggested-type list
// (suggestion is advisory), per-goal assign control over the loan's ACTIVE
// vendor assignments (any vendor), per-group bulk-assign, and the AI
// "Propose goals" run (loan-wide default, per-type narrowing) with full
// provenance surfacing. Unassigned goals are summarized in a pinned banner —
// they never reach any vendor surface until assigned. Deep links:
// ?tab=goals&goal=<id> (and ?vgReport=<id> for the VG-M1 produce card,
// which homes here — PR 3).

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2, Plus, Sparkles, X } from "lucide-react";
import { Button, Card, EmptyState, StatusBadge } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { VendorTypeOptGroups } from "@/components/portfolioAdmin/vendors/VendorsListFilters";
import type {
  LoanGoalDistillationResultDto,
  LoanGoalDto,
  LoanGoalVendorOptionDto,
} from "@/api/portfolioAdmin/loanGoalsApi";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import {
  useApproveLoanGoal,
  useAssignLoanGoal,
  useBulkAssignLoanGoals,
  useDiscardLoanGoal,
  useDistillLoanGoals,
  useLoanGoalsOverview,
  useUndiscardLoanGoal,
} from "@/hooks/portfolioAdmin/useLoanGoals";
import {
  GOAL_PRIORITY_BADGE,
  GOAL_STATUS_BADGE,
  isOpenGoal,
  isUnassignedOpenGoal,
  priorityRank,
  VENDOR_GOAL_SOFT_CAP,
  vendorTypeGroupKey,
  vendorTypeLabel,
  vendorTypeOrder,
} from "./goalPresentation";
import { GoalDrawer } from "./GoalDrawer";
import { GoalFormModal } from "./GoalFormModal";
import { VgM1ProduceCard } from "./VgM1ProduceCard";

interface GoalsTabProps {
  loan: LoanSummaryDto;
}

interface GoalGroup {
  code: string | null;
  label: string;
  goals: LoanGoalDto[];
}

export function GoalsTab({ loan }: GoalsTabProps) {
  const auth = useAuth();
  const canManage = auth.hasPermission("Vendors.Manage") || auth.hasRoleCode("ADMIN");
  const toast = useToast();
  const overview = useLoanGoalsOverview(loan.id);
  const distill = useDistillLoanGoals(loan.id);
  const assign = useAssignLoanGoal(loan.id);
  const approve = useApproveLoanGoal(loan.id);
  const discard = useDiscardLoanGoal(loan.id);
  const undiscard = useUndiscardLoanGoal(loan.id);

  const [params, setParams] = useSearchParams();
  const openGoalId = params.get("goal");
  const setOpenGoal = (goalId: string | null) => {
    const updated = new URLSearchParams(params);
    if (goalId) updated.set("goal", goalId);
    else updated.delete("goal");
    setParams(updated, { replace: true });
  };

  const [scope, setScope] = useState<string>("");
  const [showClosed, setShowClosed] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkGroup, setBulkGroup] = useState<GoalGroup | null>(null);
  const [discardGoal, setDiscardGoal] = useState<LoanGoalDto | null>(null);
  const [lastRun, setLastRun] = useState<LoanGoalDistillationResultDto | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const goals = useMemo(() => overview.data?.goals ?? [], [overview.data]);
  const vendorOptions = overview.data?.vendorOptions ?? [];
  const visibleGoals = useMemo(
    () => (showClosed ? goals : goals.filter(isOpenGoal)),
    [goals, showClosed],
  );
  const groups = useMemo<GoalGroup[]>(() => {
    const byCode = new Map<string | null, LoanGoalDto[]>();
    for (const goal of visibleGoals) {
      // "Other" and null are ONE group — two cards with the same label
      // would split the bulk-assign action.
      const code = vendorTypeGroupKey(goal.suggestedVendorType);
      const list = byCode.get(code) ?? [];
      list.push(goal);
      byCode.set(code, list);
    }
    return [...byCode.entries()]
      .map(([code, list]) => ({ code, label: vendorTypeLabel(code), goals: list }))
      .sort((a, b) => vendorTypeOrder(a.code) - vendorTypeOrder(b.code));
  }, [visibleGoals]);
  const unassignedOpen = useMemo(
    () => goals.filter(isUnassignedOpenGoal).length,
    [goals],
  );
  const openGoal = openGoalId ? goals.find((g) => g.id === openGoalId) ?? null : null;

  const runDistill = async () => {
    if (distill.isPending) return; // never double-fire
    setRunError(null);
    try {
      const result = await distill.mutateAsync({ vendorType: scope || null });
      setLastRun(result);
      if (result.sourcePath === "none") {
        toast.info(result.sourceDescription);
      } else {
        toast.success(`${result.createdGoals.length} goal(s) proposed for review.`);
      }
    } catch (err) {
      setRunError(
        err instanceof ApiError ? err.message : "AI goal distillation failed — try again.",
      );
    }
  };

  /** Soft cap: warn (never block) when an assignment action gives one
   *  vendor more than VENDOR_GOAL_SOFT_CAP open goals on this loan. */
  const warnIfOverCap = (partyAssignmentId: string, addedGoalIds: string[]) => {
    const resulting = goals.filter(
      (g) =>
        isOpenGoal(g) &&
        (g.assignedPartyAssignmentId === partyAssignmentId ||
          addedGoalIds.includes(g.id)),
    ).length;
    if (resulting > VENDOR_GOAL_SOFT_CAP) {
      toast.info(
        `This vendor will have ${resulting} open goals on this loan — consider prioritizing.`,
      );
    }
  };

  const onAssign = async (goal: LoanGoalDto, partyAssignmentId: string | null) => {
    try {
      await assign.mutateAsync({ goalId: goal.id, partyAssignmentId });
      toast.success(partyAssignmentId ? "Goal assigned." : "Goal unassigned.");
      if (partyAssignmentId) warnIfOverCap(partyAssignmentId, [goal.id]);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not assign the goal.");
    }
  };

  const onApprove = async (goal: LoanGoalDto) => {
    try {
      await approve.mutateAsync({ goalId: goal.id });
      toast.success("Goal published to the borrower.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not publish the goal.");
    }
  };

  const onUndiscard = async (goal: LoanGoalDto) => {
    try {
      await undiscard.mutateAsync({ goalId: goal.id });
      toast.success("Goal restored to proposed.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not restore the goal.");
    }
  };

  if (overview.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading goals…</p>
      </Card>
    );
  }
  if (overview.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0">
          Could not load the loan&apos;s goals. Refresh to retry.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="goals-tab">
      <Card>
        <div className="flex items-start justify-between gap-space-3 flex-wrap">
          <div>
            <h3 className="font-display font-bold text-h3 m-0">Borrower goals</h3>
            <p className="text-small text-gray-mid m-0 mt-space-1">
              Improvement goals distilled from underwriting — review, publish to
              the borrower, and assign each to the vendor accountable for it.
            </p>
          </div>
          {canManage && (
            <div className="flex items-center gap-space-2 flex-wrap">
              <label htmlFor="goal-distill-scope" className="sr-only">
                Propose scope
              </label>
              <select
                id="goal-distill-scope"
                className="rounded-input border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                disabled={distill.isPending}
                data-testid="goal-distill-scope"
              >
                <option value="">Loan-wide (all assigned vendor types)</option>
                <VendorTypeOptGroups />
              </select>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void runDistill()}
                disabled={distill.isPending}
                data-testid="goal-distill-button"
              >
                {distill.isPending ? (
                  <Loader2 size={14} aria-hidden className="animate-spin" />
                ) : (
                  <Sparkles size={14} aria-hidden />
                )}{" "}
                Propose goals (AI)
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setCreateOpen(true)}
                data-testid="goal-add-button"
              >
                <Plus size={14} aria-hidden /> Add goal
              </Button>
            </div>
          )}
        </div>

        {distill.isPending && (
          <p
            className="text-small text-gray-mid m-0 mt-space-2"
            role="status"
            data-testid="goal-distill-pending"
          >
            Analyzing underwriting reports — this can take a minute or two.
          </p>
        )}
        {runError && !distill.isPending && (
          <p className="text-small text-status-error m-0 mt-space-2" role="alert">
            {runError}{" "}
            <button
              type="button"
              className="text-gold-shadow hover:underline"
              onClick={() => void runDistill()}
              data-testid="goal-distill-retry"
            >
              Retry
            </button>
          </p>
        )}
        {lastRun && !distill.isPending && !runError && (
          <div data-testid="goal-distill-provenance" className="mt-space-2">
            <p className="text-small text-gray-mid m-0">
              Last run: {lastRun.sourceDescription}
              {lastRun.sourcePath !== "none"
                ? ` — ${lastRun.createdGoals.length} goal(s) proposed.`
                : null}
            </p>
            <p
              className="text-caption text-gray-mid m-0 mt-[2px]"
              data-testid="goal-distill-diagnostics"
            >
              {lastRun.diagnostics}
            </p>
          </div>
        )}
        {unassignedOpen > 0 && (
          <p
            className="text-small m-0 mt-space-2 rounded-card border border-status-warning bg-status-warning/10 px-space-3 py-space-2"
            data-testid="goals-unassigned-banner"
          >
            {unassignedOpen} open goal{unassignedOpen === 1 ? "" : "s"} not yet
            assigned to a vendor — unassigned goals never appear in any
            vendor&apos;s monthly report.
          </p>
        )}
      </Card>

      {goals.length === 0 ? (
        <Card>
          <EmptyState
            title="No goals yet"
            description={
              canManage
                ? "Propose goals with AI from the deal's final underwriting reports, or add one manually."
                : "Goals will appear here once the vendor manager proposes them."
            }
          />
        </Card>
      ) : (
        <>
          {visibleGoals.length === 0 && (
            <Card>
              <p className="text-small text-gray-mid italic m-0">
                All goals on this loan are closed.
              </p>
            </Card>
          )}
          {groups.map((group) => (
            <GoalGroupCard
              key={group.code ?? "other"}
              group={group}
              vendorOptions={vendorOptions}
              canManage={canManage}
              actionPending={assign.isPending || approve.isPending || discard.isPending || undiscard.isPending}
              onAssign={onAssign}
              onApprove={onApprove}
              onDiscard={(g) => setDiscardGoal(g)}
              onUndiscard={onUndiscard}
              onOpen={(g) => setOpenGoal(g.id)}
              onBulkAssign={() => setBulkGroup(group)}
            />
          ))}
        </>
      )}

      <div className="flex items-center justify-between gap-space-3 flex-wrap">
        <label className="flex items-center gap-space-1 text-small text-gray-mid">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
            data-testid="goals-show-closed"
          />
          Show inactive goals (achieved, retired, discarded)
        </label>
      </div>

      <VgM1ProduceCard loanId={loan.id} />

      {openGoal && (
        <GoalDrawer
          loanId={loan.id}
          goal={openGoal}
          canManage={canManage}
          open={!!openGoal}
          onClose={() => setOpenGoal(null)}
        />
      )}
      {createOpen && (
        <GoalFormModal
          loanId={loan.id}
          goal={null}
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {bulkGroup && (
        <BulkAssignModal
          loanId={loan.id}
          group={bulkGroup}
          vendorOptions={vendorOptions}
          onAssigned={warnIfOverCap}
          onClose={() => setBulkGroup(null)}
        />
      )}
      {discardGoal && (
        <DiscardGoalModal
          loanId={loan.id}
          goal={discardGoal}
          onClose={() => setDiscardGoal(null)}
        />
      )}
    </div>
  );
}

interface GoalGroupCardProps {
  group: GoalGroup;
  vendorOptions: LoanGoalVendorOptionDto[];
  canManage: boolean;
  actionPending: boolean;
  onAssign: (goal: LoanGoalDto, partyAssignmentId: string | null) => Promise<void>;
  onApprove: (goal: LoanGoalDto) => Promise<void>;
  onDiscard: (goal: LoanGoalDto) => void;
  onUndiscard: (goal: LoanGoalDto) => Promise<void>;
  onOpen: (goal: LoanGoalDto) => void;
  onBulkAssign: () => void;
}

function GoalGroupCard({
  group,
  vendorOptions,
  canManage,
  actionPending,
  onAssign,
  onApprove,
  onDiscard,
  onUndiscard,
  onOpen,
  onBulkAssign,
}: GoalGroupCardProps) {
  // Priority tiers first (High → Standard → Low — publish the top tier,
  // discard the tail in seconds), unassigned before assigned within a tier.
  const ordered = useMemo(
    () =>
      [...group.goals].sort((a, b) => {
        const pr = priorityRank(a.priority) - priorityRank(b.priority);
        if (pr !== 0) return pr;
        const ua = a.assignedPartyAssignmentId ? 1 : 0;
        const ub = b.assignedPartyAssignmentId ? 1 : 0;
        return ua - ub;
      }),
    [group.goals],
  );
  const unassigned = group.goals.filter(isUnassignedOpenGoal);

  return (
    <Card data-testid={`goal-group-${group.code ?? "other"}`}>
      <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-2">
        <h4 className="font-display font-bold text-small m-0">
          {group.label}{" "}
          <span className="text-gray-mid font-normal">({group.goals.length})</span>
        </h4>
        {canManage && unassigned.length > 0 && vendorOptions.length > 0 && (
          <Button
            variant="text"
            size="sm"
            onClick={onBulkAssign}
            data-testid={`goal-group-bulk-${group.code ?? "other"}`}
          >
            Assign {unassigned.length} unassigned…
          </Button>
        )}
      </div>
      <ul className="m-0 p-0 list-none divide-y divide-border-light">
        {ordered.map((goal) => (
          <li
            key={goal.id}
            className={`py-space-2 flex items-center gap-space-3 flex-wrap${
              goal.priority === "low" && isOpenGoal(goal) ? " opacity-60" : ""
            }`}
          >
            <button
              type="button"
              className="text-left flex-1 min-w-[200px]"
              onClick={() => onOpen(goal)}
              data-testid={`goal-row-${goal.id}`}
            >
              <span className="text-small font-medium block">{goal.title}</span>
              <span className="text-caption text-gray-mid block mt-[2px]">
                {goal.source === "ai" ? "AI-proposed" : "Staff-created"}
                {goal.targetMonths ? ` · months ${goal.targetMonths}` : null}
                {goal.overlapNote ? " · overlaps another goal" : null} · No
                grades yet
              </span>
            </button>
            <StatusBadge
              tone={GOAL_PRIORITY_BADGE[goal.priority].tone}
              data-testid={`goal-priority-${goal.id}`}
            >
              {GOAL_PRIORITY_BADGE[goal.priority].label}
            </StatusBadge>
            <StatusBadge
              tone={GOAL_STATUS_BADGE[goal.status].tone}
              struck={goal.status === "retired" || goal.status === "discarded"}
            >
              {GOAL_STATUS_BADGE[goal.status].label}
            </StatusBadge>
            {canManage && goal.status === "proposed" && (
              <span className="flex items-center gap-space-1">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void onApprove(goal)}
                  disabled={actionPending}
                  title="Approve & publish to the borrower"
                  data-testid={`goal-quick-approve-${goal.id}`}
                >
                  <Check size={14} aria-hidden /> Publish
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onDiscard(goal)}
                  disabled={actionPending}
                  title="Discard this proposed goal (recoverable)"
                  data-testid={`goal-quick-discard-${goal.id}`}
                >
                  <X size={14} aria-hidden /> Discard
                </Button>
              </span>
            )}
            {canManage && goal.status === "discarded" && (
              <Button
                variant="text"
                size="sm"
                onClick={() => void onUndiscard(goal)}
                disabled={actionPending}
                data-testid={`goal-quick-undiscard-${goal.id}`}
              >
                Restore
              </Button>
            )}
            {canManage && isOpenGoal(goal) ? (
              <select
                className="rounded-input border border-border-light bg-white-pure px-space-2 py-space-1 text-small max-w-[220px]"
                value={goal.assignedPartyAssignmentId ?? ""}
                onChange={(e) => void onAssign(goal, e.target.value || null)}
                disabled={actionPending}
                aria-label={`Assign "${goal.title}" to a vendor`}
                data-testid={`goal-assign-${goal.id}`}
              >
                <option value="">Unassigned</option>
                {vendorOptions.map((v) => (
                  <option key={v.partyAssignmentId} value={v.partyAssignmentId}>
                    {v.vendorName} ({v.roleOnLoan})
                  </option>
                ))}
                {goal.assignedPartyAssignmentId &&
                  !vendorOptions.some(
                    (v) => v.partyAssignmentId === goal.assignedPartyAssignmentId,
                  ) && (
                    <option value={goal.assignedPartyAssignmentId}>
                      {goal.assignedVendorName ?? "Assigned vendor"} (inactive)
                    </option>
                  )}
              </select>
            ) : (
              <span className="text-small text-gray-mid min-w-[120px]">
                {goal.assignedVendorName ?? "Unassigned"}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

interface BulkAssignModalProps {
  loanId: string;
  group: GoalGroup;
  vendorOptions: LoanGoalVendorOptionDto[];
  /** Soft-cap check, called with the target assignment + assigned goal ids. */
  onAssigned: (partyAssignmentId: string, goalIds: string[]) => void;
  onClose: () => void;
}

function BulkAssignModal({ loanId, group, vendorOptions, onAssigned, onClose }: BulkAssignModalProps) {
  const toast = useToast();
  const bulkAssign = useBulkAssignLoanGoals(loanId);
  const [assignmentId, setAssignmentId] = useState("");
  const unassigned = group.goals.filter(isUnassignedOpenGoal);

  const onSubmit = async () => {
    if (!assignmentId) {
      toast.error("Choose a vendor.");
      return;
    }
    try {
      const goalIds = unassigned.map((g) => g.id);
      await bulkAssign.mutateAsync({ goalIds, partyAssignmentId: assignmentId });
      toast.success(`${unassigned.length} goal(s) assigned.`);
      onAssigned(assignmentId, goalIds);
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Bulk assign failed.");
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Assign ${group.label} goals`}
      description={`Assigns the ${unassigned.length} unassigned goal(s) in this group to one vendor. The suggestion is advisory — any assigned vendor can carry them.`}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={bulkAssign.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void onSubmit()}
            disabled={bulkAssign.isPending}
            data-testid="goal-bulk-confirm"
          >
            {bulkAssign.isPending ? "Assigning…" : "Assign goals"}
          </Button>
        </div>
      }
    >
      <label htmlFor="goal-bulk-vendor" className="block text-small mb-space-1">
        Vendor
      </label>
      <select
        id="goal-bulk-vendor"
        className="w-full rounded-input border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
        value={assignmentId}
        onChange={(e) => setAssignmentId(e.target.value)}
        data-testid="goal-bulk-vendor"
      >
        <option value="">Choose a vendor…</option>
        {vendorOptions.map((v) => (
          <option key={v.partyAssignmentId} value={v.partyAssignmentId}>
            {v.vendorName} ({v.roleOnLoan})
          </option>
        ))}
      </select>
    </Modal>
  );
}

interface DiscardGoalModalProps {
  loanId: string;
  goal: LoanGoalDto;
  onClose: () => void;
}

function DiscardGoalModal({ loanId, goal, onClose }: DiscardGoalModalProps) {
  const toast = useToast();
  const discard = useDiscardLoanGoal(loanId);
  const [reason, setReason] = useState("");

  const onSubmit = async () => {
    try {
      await discard.mutateAsync({ goalId: goal.id, reason: reason.trim() || null });
      toast.success("Goal discarded.");
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not discard the goal.");
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Discard goal"
      description={`"${goal.title}" returns to view under "Show inactive goals" and can be restored to proposed at any time. Discarded goals never reach the borrower or any vendor.`}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={discard.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void onSubmit()}
            disabled={discard.isPending}
            data-testid="goal-discard-confirm"
          >
            {discard.isPending ? "Discarding…" : "Discard goal"}
          </Button>
        </div>
      }
    >
      <label htmlFor="goal-discard-reason" className="block text-small mb-space-1">
        Reason (optional)
      </label>
      <input
        id="goal-discard-reason"
        className="w-full rounded-input border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={300}
        placeholder="One line — why this goal doesn't make the cut."
        data-testid="goal-discard-reason"
      />
    </Modal>
  );
}
